import { CUSTOMER_LEVEL } from '@wyz/types';
import { FEATURES, ROLES } from '~/common/helpers';
import {
  CustomerAccessControl,
  CustomerFormPrivileges,
  CustomerFormPrivilegesContext,
  CustomerFormStepPrivileges,
} from '~/connect/types';

type PrivilegesScope = {
  acl: CustomerAccessControl;
  isWyzUser: boolean;
  // if the customerId IS NOT in the attached entities AND the user can see/edit it, we can assume it's a child of an attached customers
  // AND if there is NO customerId => we're actually creating customer and it will for sure be a child for one of my attached customers
  isChild: boolean;
  inMyAttachedEntities: boolean;
  isEditing: boolean;
  isN1: boolean;
  isN2plus: boolean;
  /** `auth.isWyzUser || isChild`, the most repeated rule of the form */
  canWriteAsOwner: boolean;
  hasCustomerReadWriteAccess: boolean;
};

const CUSTOMER_READ_WRITE_ROLES = [
  ROLES.ROLE_CUSTOMER_CREATE,
  ROLES.ROLE_CUSTOMER_UPDATE,
  ROLES.ROLE_CUSTOMER_GET,
];

const EDI_WHEEL_ROLES = [
  ROLES.ROLE_CUSTOMER_EDI_WHEEL_CREATE,
  ROLES.ROLE_CUSTOMER_EDI_WHEEL_UPDATE,
];

const CONTAINER_ELIGIBILITY_ROLES = [
  ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_CREATE,
  ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_UPDATE,
];

const buildScope = ({
  acl,
  auth,
  entities,
  level,
  customer,
}: CustomerFormPrivilegesContext): PrivilegesScope => {
  const attachedEntityIds = entities.map((entity) => entity.id);
  const isAttached = customer ? attachedEntityIds.includes(customer.id) : true;
  const isChild = customer ? !isAttached : true;

  return {
    acl,
    isWyzUser: auth.isWyzUser,
    isChild,
    inMyAttachedEntities: isAttached,
    isEditing: Boolean(customer?.id),
    isN1: level === CUSTOMER_LEVEL.N1,
    isN2plus: level > CUSTOMER_LEVEL.N1,
    canWriteAsOwner: auth.isWyzUser || isChild,
    hasCustomerReadWriteAccess: acl.isGranted(
      CUSTOMER_READ_WRITE_ROLES,
      'affirmative',
    ),
  };
};

const buildGeneralFormPrivileges = (
  scope: PrivilegesScope,
  customer?: CustomerFormPrivilegesContext['customer'],
): CustomerFormStepPrivileges<'GENERAL_FORM'> => ({
  visible: scope.hasCustomerReadWriteAccess,
  writable:
    scope.acl.isGranted(
      [ROLES.ROLE_CUSTOMER_CREATE, ROLES.ROLE_CUSTOMER_UPDATE],
      'affirmative',
    ) ||
    scope.isWyzUser ||
    scope.isChild,
  fields: {
    LABEL_FRONT_CHILDREN: {
      visible: Boolean(customer?.hasChildren),
      writable: true,
    },
    LOGO: {
      visible: true,
      writable:
        scope.inMyAttachedEntities ||
        scope.isWyzUser ||
        scope.acl.isGranted(
          [ROLES.ROLE_CUSTOMER_LOGO_CREATE, ROLES.ROLE_CUSTOMER_LOGO_UPDATE],
          'unanimous',
        ),
    },
    GROUP_NAME: {
      visible:
        scope.isN1 &&
        scope.acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_CUSTOMER_GROUP_CREATE,
            ROLES.ROLE_CUSTOMER_CUSTOMER_GROUP_UPDATE,
          ],
          'unanimous',
        ),
      writable: scope.isWyzUser,
    },
    REDISTRIBUTION_MAX_DELAY: {
      visible: scope.isN1 && scope.canWriteAsOwner,
      writable: scope.isN1 && scope.canWriteAsOwner,
    },
    LOCALE: {
      visible: scope.canWriteAsOwner,
      writable: scope.canWriteAsOwner,
    },
    // Create a special field for special cases
    DEFAULT_GENERAL_FIELDS: {
      visible: scope.canWriteAsOwner,
      writable: scope.canWriteAsOwner,
    },
    CUSTOMER_CODE: {
      visible: true,
      writable: scope.isWyzUser,
    },
  },
});

const buildDeliveryFormPrivileges = (
  scope: PrivilegesScope,
): CustomerFormStepPrivileges<'DELIVERY_FORM'> => {
  const ownerWritableField = {
    visible: true,
    writable: scope.canWriteAsOwner,
  };

  return {
    visible: scope.hasCustomerReadWriteAccess,
    writable: scope.canWriteAsOwner,
    fields: {
      VAT_ZONE: {
        visible:
          scope.acl.isGranted(
            [
              ROLES.ROLE_CUSTOMER_VAT_ZONE_CREATE,
              ROLES.ROLE_CUSTOMER_VAT_ZONE_UPDATE,
            ],
            'unanimous',
          ) && scope.isN1,
        writable: scope.canWriteAsOwner,
      },
      BILLING_CODE: {
        visible: scope.acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_BILLING_CODE_CREATE,
            ROLES.ROLE_CUSTOMER_BILLING_CODE_UPDATE,
          ],
          'unanimous',
        ),
        writable: scope.canWriteAsOwner,
      },
      ADDITIONAL_ADDRESS: { ...ownerWritableField },
      ADDRESS: { ...ownerWritableField },
      CITY: { ...ownerWritableField },
      COUNTRY: { ...ownerWritableField },
      ECOTAX_ZONE: { ...ownerWritableField },
      ZIP_CODE: { ...ownerWritableField },
    },
  };
};

const buildConfigurationFormPrivileges = (
  scope: PrivilegesScope,
): CustomerFormStepPrivileges<'CONFIGURATION_FORM'> => {
  const canManageContainerEligibility = scope.acl.isGranted(
    CONTAINER_ELIGIBILITY_ROLES,
    'affirmative',
  );
  const ediWheelField = {
    visible: scope.isWyzUser || scope.acl.isGranted(EDI_WHEEL_ROLES),
    writable: true,
  };

  return {
    visible:
      scope.isWyzUser ||
      (scope.isN1 && canManageContainerEligibility) ||
      (scope.isN2plus &&
        scope.acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_AUTO_VALIDATION_UPDATE,
            ROLES.ROLE_CUSTOMER_DMS_REF_UPDATE,
            ...CONTAINER_ELIGIBILITY_ROLES,
          ],
          'affirmative',
        )),
    writable:
      scope.isWyzUser ||
      (scope.isChild &&
        scope.acl.isGranted([
          ROLES.ROLE_CUSTOMER_DMS_REF_UPDATE,
          ROLES.ROLE_CUSTOMER_AUTO_VALIDATION_UPDATE,
        ])) ||
      scope.acl.isGranted(
        [...EDI_WHEEL_ROLES, ...CONTAINER_ELIGIBILITY_ROLES],
        'affirmative',
      ),
    fields: {
      BUYER_ID: { ...ediWheelField },
      ORDERING_ID: { ...ediWheelField },
      CONSIGNEE_ID: { ...ediWheelField },
      DMS_REFERENCE: {
        visible:
          scope.isN2plus &&
          (scope.isWyzUser ||
            scope.acl.isGranted([ROLES.ROLE_CUSTOMER_DMS_REF_UPDATE])),
        writable: scope.isChild,
      },
      AUTO_VALIDATION: {
        visible:
          scope.isN2plus &&
          (scope.isWyzUser ||
            scope.acl.isGranted([ROLES.ROLE_CUSTOMER_AUTO_VALIDATION_UPDATE])),
        writable: scope.isChild,
      },
      ELIGIBLE_TO_CONTAINER_ORDERS: {
        visible:
          scope.isN1 && (scope.isWyzUser || canManageContainerEligibility),
        writable:
          scope.isN2plus && (scope.isWyzUser || canManageContainerEligibility),
      },
    },
  };
};

export const buildCustomerFormPrivileges = (
  context: CustomerFormPrivilegesContext,
): CustomerFormPrivileges => {
  const scope = buildScope(context);
  const { acl } = scope;
  const isRedistributionEnabled = acl.isEnabledFeatures([
    FEATURES.REDISTRIBUTION,
  ]);
  const isQuoteEnabled = acl.isEnabledFeatures([FEATURES.QUOTE]);
  const isQuoteExpressGranted =
    acl.isEnabledFeatures([FEATURES.QUOTE_EXPRESS]) &&
    acl.isGranted([ROLES.ROLE_CUSTOMER_QUOTE_FORM], 'affirmative');

  return {
    GENERAL_FORM: buildGeneralFormPrivileges(scope, context.customer),
    CRITERIA_FORM: {
      visible: scope.hasCustomerReadWriteAccess,
      writable: scope.canWriteAsOwner,
    },
    QUOTE_EXPRESS_FORM: {
      writable: isQuoteExpressGranted,
      visible: isQuoteExpressGranted,
    },
    CONTACT_FORM: {
      visible: scope.hasCustomerReadWriteAccess,
      writable: true,
    },
    DELIVERY_FORM: buildDeliveryFormPrivileges(scope),
    CONFIGURATION_FORM: buildConfigurationFormPrivileges(scope),
    SUPPLIERS_FORM: {
      visible:
        scope.isN1 &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_SUPPLIER_TEMPLATE_CREATE,
            ROLES.ROLE_CUSTOMER_SUPPLIER_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: scope.isWyzUser,
    },
    BRANDS_FORM: {
      visible:
        scope.isN1 &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_BRAND_TEMPLATE_CREATE,
            ROLES.ROLE_CUSTOMER_BRAND_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: scope.canWriteAsOwner,
    },
    QUOTE_BRANDS_FORM: {
      visible:
        isQuoteEnabled &&
        acl.isGranted(
          [
            ROLES.ROLE_QUOTE_BRAND_TEMPLATE_CREATE,
            ROLES.ROLE_QUOTE_BRAND_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: scope.canWriteAsOwner,
    },
    REDISTRIBUTION_BRANDS_FORM: {
      visible:
        scope.isN2plus &&
        isRedistributionEnabled &&
        acl.isGranted(
          [
            ROLES.ROLE_REDISTRIBUTION_BRAND_TEMPLATE_CREATE,
            ROLES.ROLE_REDISTRIBUTION_BRAND_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: scope.canWriteAsOwner,
    },
    SETTING_TEMPLATE_FORM: {
      visible:
        scope.isN1 &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_SETTING_TEMPLATE_CREATE,
            ROLES.ROLE_CUSTOMER_SETTING_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: scope.canWriteAsOwner,
    },
    INTERNAL_STOCK_FORM: {
      visible:
        scope.isEditing &&
        scope.isWyzUser &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_INTERNAL_STOCK_CREATE,
            ROLES.ROLE_CUSTOMER_INTERNAL_STOCK_UPDATE,
          ],
          'affirmative',
        ),
      writable: scope.isEditing && scope.isWyzUser,
    },
    STATUS_FORM: {
      visible: scope.canWriteAsOwner,
      writable: scope.canWriteAsOwner,
    },
  };
};
